import './BetbookExtendedFilter.less';

import clsx from 'clsx';
import { useReframe } from 'nike-re-framejs';
import React, { useEffect, useMemo, useState } from 'react';

import i18n from '../../../common/i18n';
import { trackEvent } from '../../../analytics/gtm';
import { Button } from '../../button/Button';
import BetbookFilterTags from './BetbookFilterTags';
import RangeSlider from '../../range-slider/views/RangeSlider';
import SportsSelector from '../../sports-selector/views/SportsSelector';

import {
    betbookFilterBetCount$,
    betbookFilterExpirationTag$,
    betbookFilterOdds$,
    betbookFilterResultsCount$,
    betbookFiltersConfig$,
    betbookFilterSportIds$,
    betbookFilterStake$,
    betbookFilterWin$
} from '../subs';
import { fetchBetbookFilterConfig, fetchBetbookFilterResultsCount } from '../handlers';
import { POST_TICKET } from '../constants';

function combineWithConfig(range1, cfg) {
    if (range1[0] !== undefined && range1[1] !== undefined) return range1;
    const range2 = [cfg.min, cfg.max];
    let [min = range2[0], max = range2[1]] = range1;
    return [min, max];
}
function mergeWithConfig(range, { min, max } = {}) {
    if (range?.length) {
        min = min && min >= range[0] ? undefined : range[0];
        max = max && max <= range[1] ? undefined : range[1];
    }
    return [min, max];
}
function marksToObject(marks) {
    return marks.reduce((ret, mark) => {
        ret[mark] = mark;
        return ret;
    }, {});
}
function compose([min, max], cfg) {
    return [min === cfg.min ? undefined : min, max === cfg.max ? undefined : max];
}
function init(range) {
    return range ? [...range] : [undefined, undefined];
}
function rangeEquals(range1, range2) {
    return range1.every((val, idx) => val === range2[idx]);
}

function BetbookExtendedFilter({
    className,
    onReset,
    onSubmit,
    permanentlyVisibleSports,
    postType,
    stickyFooter: sticky,
    twoColumnSportsFilter,
    twoColumnSlidersFilter
}) {
    const { subscribe } = useReframe();
    const appliedBetCount = subscribe(betbookFilterBetCount$);
    const appliedExpirationTag = subscribe(betbookFilterExpirationTag$);
    const appliedOdds = subscribe(betbookFilterOdds$);
    const appliedSportIds = subscribe(betbookFilterSportIds$);
    const appliedStake = subscribe(betbookFilterStake$);
    const appliedWin = subscribe(betbookFilterWin$);
    const cfg = subscribe(betbookFiltersConfig$);
    const resultsCount = subscribe(betbookFilterResultsCount$);

    const [expirationTag, setExpirationTag] = useState(appliedExpirationTag || null);
    const [sportIds, setSportIds] = useState(appliedSportIds ? [...appliedSportIds] : []);
    const [betCount, setBetCount] = useState(init(appliedBetCount));
    const [odds, setOdds] = useState(init(appliedOdds));
    const [stake, setStake] = useState(init(appliedStake));
    const [win, setWin] = useState(init(appliedWin));
    const [shadow, setShadow] = useState(false);

    const hideRangeSliders = Object.keys(cfg || {}).length === 0;

    /** Observer, ktorý sleduje či je element scrollovateľný */
    const dialogBodyObserver = useMemo(
        () =>
            new ResizeObserver(entries => {
                window.requestAnimationFrame(() => {
                    if (!Array.isArray(entries) || !entries.length) {
                        return;
                    }
                    const { clientHeight, scrollHeight } = entries[0].target;
                    setShadow(scrollHeight > clientHeight);
                });
            }),
        []
    );

    useEffect(() => {
        if (!cfg) return;
        let value = mergeWithConfig(betCount, cfg.betCount);
        if (!rangeEquals(value, betCount)) setBetCount(value);
        value = mergeWithConfig(odds, cfg.odds);
        if (!rangeEquals(value, odds)) setOdds(value);
        value = mergeWithConfig(stake, cfg.stake);
        if (!rangeEquals(value, stake)) setStake(value);
        value = mergeWithConfig(win, cfg.win);
        if (!rangeEquals(value, win)) setWin(value);
        value = sportIds.filter(id => cfg.sportIds?.includes(id));
        if (value.length !== sportIds.length) setSportIds(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg]);

    useEffect(() => {
        const dialogBody = document.querySelector('.dialog-body');
        if (dialogBody) {
            dialogBodyObserver.observe(dialogBody);
            return () => dialogBodyObserver.unobserve(dialogBody);
        }
    }, [dialogBodyObserver]);

    useEffect(() => {
        fetchBetbookFilterResultsCount(betCount, expirationTag, odds, sportIds, stake, postType, win);
    }, [expirationTag, betCount, odds, sportIds, stake, postType, win]);

    function onBetCountRangeChange(range) {
        trackEvent('Betbook Filter', 'Change', 'Extended RangeSlider - betCount');
        const newValue = compose(range, cfg.betCount);
        if (!rangeEquals(newValue, betCount)) setBetCount(newValue);
    }
    function onOddsRangeChange(range) {
        trackEvent('Betbook Filter', 'Change', 'Extended RangeSlider - odds');
        const newValue = compose(range, cfg.odds);
        if (!rangeEquals(newValue, odds)) setOdds(newValue);
    }
    function onStakeRangeChange(range) {
        trackEvent('Betbook Filter', 'Change', 'Extended RangeSlider - stake');
        const newValue = compose(range, cfg.stake);
        if (!rangeEquals(newValue, stake)) setStake(newValue);
    }
    function onWinRangeChange(range) {
        trackEvent('Betbook Filter', 'Change', 'Extended RangeSlider - win');
        const newValue = compose(range, cfg.win);
        if (!rangeEquals(newValue, win)) setWin(newValue);
    }
    function onSportsChange(newSportIds) {
        if (newSportIds.length) {
            if (sportIds.length > newSportIds.length) {
                const sportId = sportIds.find(s => !newSportIds.includes(s));
                trackEvent('Betbook Filter', 'Set', 'Extended SportSelect - ' + sportId, 1);
            } else {
                const sportId = newSportIds.find(s => !sportIds.includes(s));
                trackEvent('Betbook Filter', 'Remove', 'Extended SportSelect - ' + sportId);
            }
        } else {
            trackEvent('Betbook Filter', 'Set', 'Extended SportSelect - all', 1);
        }
        fetchBetbookFilterConfig(postType, expirationTag, newSportIds);
        setSportIds(newSportIds);
    }
    function onFilterSubmit() {
        onSubmit({ betCount, expirationTag, odds, sportIds, stake, win });
        trackEvent('Betbook Filter', 'Submit', 'Extended');
    }
    function onFilterReset() {
        fetchBetbookFilterConfig(postType);
        setExpirationTag(null);
        setSportIds([]);
        setBetCount(init());
        setOdds(init());
        setStake(init());
        setWin(init());
        onReset();
        trackEvent('Betbook Filter', 'Reset', 'Extended');
    }
    function onTagChange(tag) {
        setExpirationTag(tag);
        fetchBetbookFilterConfig(postType, tag, sportIds);
        if (tag) {
            trackEvent('Betbook Filter', 'Set', 'Extended Tag - ' + tag, 1);
        } else {
            trackEvent('Betbook Filter', 'Reset', 'Extended Tags');
        }
    }
    return cfg ? (
        <>
            <div className={className}>
                <div className="mb-5 fs-14 lh-21 text-extra-bold">{i18n('nui.betbook.filters.display')}</div>
                <BetbookFilterTags className="mb-20" selectedTag={expirationTag} onTagChange={onTagChange} />
                {cfg.sportIds?.length ? (
                    <>
                        <div className="mb-5 fs-14 lh-21 text-extra-bold">{i18n('nui.betbook.filters.by-sport')}</div>
                        <SportsSelector
                            onSelectionChange={onSportsChange}
                            permanentlyVisibleSports={permanentlyVisibleSports}
                            selectedSportIds={sportIds}
                            sportIds={cfg.sportIds}
                            twoColumns={twoColumnSportsFilter}
                        />
                    </>
                ) : null}
                {hideRangeSliders ? null : (
                    <div className={clsx('flex', { 'flex-col': !twoColumnSlidersFilter })}>
                        <div className="flex flex-col flex-1">
                            <div className="flex flex-col mt-30">
                                <div className="betbook-extended-filter-slider-title fs-14 lh-21 text-extra-bold self-start">
                                    {i18n('nui.betbook.filters.range.odd')}
                                </div>
                                <RangeSlider
                                    className="self-center"
                                    fractionDigits={2}
                                    marks={marksToObject(cfg.odds.marks)}
                                    max={cfg.odds.max}
                                    min={cfg.odds.min}
                                    onAfterChange={onOddsRangeChange}
                                    tipFormatter={tip => `${tip.toFixed(2)}`}
                                    value={combineWithConfig(odds, cfg.odds)}
                                />
                            </div>
                            {postType === POST_TICKET && cfg.win ? (
                                <div className="flex flex-col mt-30">
                                    <div className="betbook-extended-filter-slider-title fs-14 lh-21 text-extra-bold self-start">
                                        {i18n('nui.betbook.filters.range.eventualWin')}
                                    </div>
                                    <RangeSlider
                                        className="self-center"
                                        fractionDigits={2}
                                        marks={marksToObject(cfg.win.marks)}
                                        max={cfg.win.max}
                                        min={cfg.win.min}
                                        onAfterChange={onWinRangeChange}
                                        symbol="€"
                                        tipFormatter={tip => `${tip.toFixed(2)}\u00A0€`}
                                        value={combineWithConfig(win, cfg.win)}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div
                            className={clsx('flex flex-col flex-1', {
                                'betbook-extended-filter-spacer': twoColumnSlidersFilter
                            })}
                        >
                            <div className="flex flex-col mt-30">
                                <div className="betbook-extended-filter-slider-title fs-14 lh-21 text-extra-bold self-start">
                                    {i18n('nui.betbook.filters.range.stake')}
                                </div>
                                <RangeSlider
                                    className="self-center"
                                    fractionDigits={2}
                                    marks={marksToObject(cfg.stake.marks)}
                                    max={cfg.stake.max}
                                    min={cfg.stake.min}
                                    onAfterChange={onStakeRangeChange}
                                    symbol="€"
                                    tipFormatter={tip => `${tip.toFixed(2)}\u00A0€`}
                                    value={combineWithConfig(stake, cfg.stake)}
                                />
                            </div>
                            {postType === POST_TICKET && cfg.betCount ? (
                                <div className="flex flex-col mt-30">
                                    <div className="betbook-extended-filter-slider-title fs-14 lh-21 text-extra-bold self-start">
                                        {i18n('nui.betbook.filters.range.betCount')}
                                    </div>
                                    <RangeSlider
                                        className="self-center"
                                        marks={marksToObject(cfg.betCount.marks)}
                                        max={cfg.betCount.max}
                                        min={cfg.betCount.min}
                                        onAfterChange={onBetCountRangeChange}
                                        tipFormatter={tip => `${tip}`}
                                        value={combineWithConfig(betCount, cfg.betCount)}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
            <div
                className={clsx('extended-filter-footer', {
                    sticky,
                    'shadow custom-offset': shadow,
                    'border-top': !shadow && sticky
                })}
            >
                {resultsCount > 0 ? null : (
                    <div className="fs-14 text-bold mt-20 text-center c-default-light">
                        {i18n('nui.betbook.filters.no-results')}
                    </div>
                )}
                <Button
                    className={clsx('nui-button text-normal', { 'mt-10': !sticky, 'mt-20': sticky })}
                    data-atid="betbook-extended-filter-submit"
                    disabled={resultsCount === 0}
                    label={i18n('nui.betbook.filters.extended-filter.submit')}
                    onClick={onFilterSubmit}
                    primary
                >
                    <span className="ml-5 fs-16 text-normal">({resultsCount})</span>
                </Button>
                <button
                    className="nui-blue-link pointer fs-16 mt-20"
                    onClick={onFilterReset}
                    data-atid="betbook-extended-filter-reset"
                >
                    {i18n('nui.betbook.filters.extended-filter.reset')}
                </button>
            </div>
        </>
    ) : null;
}

export default BetbookExtendedFilter;
